import { useState, useCallback } from "react";
import type { TFunction } from "i18next";
import { extractErrorMessage } from "@/shared/utils";
import { useDeleteTaskMutation, useToggleFavoriteMutation, useUpdateTaskMutation } from "../api";
import type { TaskStatus } from "../types";

interface PromptState {
  title: string;
  text: string;
  variant: "success" | "error";
}

interface UseBoardMutationsProps {
  t: TFunction;
}

export function useBoardMutations({ t }: UseBoardMutationsProps) {
  const [prompt, setPrompt] = useState<PromptState | null>(null);
  const [reorderError, setReorderError] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [statusOverrides, setStatusOverrides] = useState<Record<number, TaskStatus>>({});
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);

  const [deleteTaskMutation, { isLoading: isDeletingTask }] = useDeleteTaskMutation();
  const [toggleFavoriteMutation] = useToggleFavoriteMutation();
  const [updateTaskMutation] = useUpdateTaskMutation();

  async function handleDeleteConfirm(taskId: number, projectId: number, onSuccess: () => void) {
    try {
      await deleteTaskMutation({ projectId, id: taskId }).unwrap();
      setConfirmDelete(null);
      onSuccess();
      setPrompt({
        title: t("messages.deleteSuccessTitle"),
        text: t("messages.deleteSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setConfirmDelete(null);
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.deleteError", t),
        variant: "error",
      });
    }
  }

  async function handleToggleFavorite(taskId: number, projectId: number) {
    if (isTogglingFavorite) return;
    setIsTogglingFavorite(true);
    try {
      await toggleFavoriteMutation({ projectId, taskId }).unwrap();
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.favoriteError", t),
        variant: "error",
      });
    } finally {
      setIsTogglingFavorite(false);
    }
  }

  const handleStatusChange = useCallback(
    function handleStatusChange(taskId: number, newStatus: TaskStatus, projectId: number) {
      setStatusOverrides((prev) => ({ ...prev, [taskId]: newStatus }));

      updateTaskMutation({ projectId, id: taskId, data: { status: newStatus } })
        .unwrap()
        .then(() => {
          setStatusOverrides((prev) => {
            const next = { ...prev };
            delete next[taskId];
            return next;
          });
        })
        .catch((err: unknown) => {
          setStatusOverrides((prev) => {
            const next = { ...prev };
            delete next[taskId];
            return next;
          });
          setPrompt({
            title: t("messages.errorTitle"),
            text: extractErrorMessage(err, "messages.statusChangeError", t),
            variant: "error",
          });
        });
    },
    [updateTaskMutation, t],
  );

  function handleReorderError() {
    setReorderError(true);
  }

  return {
    prompt,
    setPrompt,
    reorderError,
    setReorderError,
    confirmDelete,
    setConfirmDelete,
    statusOverrides,
    isDeletingTask,
    handleDeleteConfirm,
    handleToggleFavorite,
    handleStatusChange,
    handleReorderError,
  };
}
